/**
 * Agents list route + /agents/new wizard dispatcher.
 *
 * The router wires both `/agents` and `/agents/new` to this component,
 * so we branch on the current pathname to render either the list or
 * the New Agent Wizard. Layout wraps us (from router.tsx) — we only
 * render the page content.
 */
import { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Plus } from 'lucide-react';
import type { AgentSummary } from '@/api/types';
import { useAgents, useAgentAction, useDeleteAgent } from '@/hooks/useAgents';
import { EmptyState } from '@/components/EmptyState';
import { CardSkeleton } from '@/components/LoadingSkeleton';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AgentListRow } from '@/components/AgentListItem';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { StepUpDialog } from '@/components/StepUpDialog';
import { GlobalOverlays } from '@/components/GlobalOverlays';
import { ApiError } from '@/api/client';
import NewAgentWizard from '@/routes/NewAgentWizard';

type FilterValue = 'all' | 'active' | 'failed';

export default function Agents() {
  const location = useLocation();
  // Router sends both /agents and /agents/new here — branch on path.
  if (location.pathname === '/agents/new') {
    return <NewAgentWizard />;
  }
  return <AgentsList />;
}

function AgentsList() {
  const [filter, setFilter] = useState<FilterValue>('all');
  const { data, isLoading, isError, refetch } = useAgents(filter);
  const action = useAgentAction();
  const del = useDeleteAgent();

  const [toDelete, setToDelete] = useState<string | null>(null);
  const [stepUpOpen, setStepUpOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const items = useMemo<AgentSummary[]>(() => (data?.items ?? []) as AgentSummary[], [data]);

  async function handleAction(name: string, actionKind: 'start' | 'stop' | 'restart') {
    try {
      await action.mutateAsync({ name, action: actionKind });
    } catch (err) {
      if (err instanceof ApiError) {
        // Surfacing through the global toaster is subagent C's territory;
        // for now we log to console so the user sees nothing unexpected.
        console.error(err.message);
      }
    }
  }

  function handleConfirmDelete() {
    setStepUpOpen(true);
  }

  async function handleStepUpSuccess(token: string) {
    if (!toDelete) return;
    try {
      await del.mutateAsync({ name: toDelete, stepupToken: token });
      setToDelete(null);
      setDeleteError(null);
    } catch (err) {
      setDeleteError(err instanceof ApiError ? err.message : 'Failed to delete agent');
    }
  }

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Agents</h1>
          <p className="text-sm text-muted-foreground">
            Manage every agent running on this host.
          </p>
        </div>
        <Button asChild>
          <Link to="/agents/new">
            <Plus className="h-4 w-4" aria-hidden />
            <span>Create agent</span>
          </Link>
        </Button>
      </div>

      <Tabs value={filter} onValueChange={(v) => setFilter(v as FilterValue)}>
        <TabsList>
          <TabsTrigger value="all">All</TabsTrigger>
          <TabsTrigger value="active">Active</TabsTrigger>
          <TabsTrigger value="failed">Failed</TabsTrigger>
        </TabsList>
      </Tabs>

      {isLoading && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          <CardSkeleton />
          <CardSkeleton />
          <CardSkeleton />
        </div>
      )}

      {isError && (
        <EmptyState
          title="Could not load agents"
          description="The backend returned an error. Try again."
          action={<Button onClick={() => refetch()}>Retry</Button>}
        />
      )}

      {!isLoading && !isError && items.length === 0 && (
        <EmptyState
          title="No agents yet"
          description="Create your first one to get started."
          action={
            <Button asChild>
              <Link to="/agents/new">
                <Plus className="h-4 w-4" aria-hidden />
                <span>Create agent</span>
              </Link>
            </Button>
          }
        />
      )}

      {!isLoading && !isError && items.length > 0 && (
        <div className="rounded-md border border-border overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-muted/30 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="py-2 px-4 font-semibold">Name</th>
                <th className="py-2 px-4 font-semibold">Description</th>
                <th className="py-2 px-4 font-semibold">Status</th>
                <th className="py-2 px-4 font-semibold">Discord</th>
                <th className="py-2 px-4 font-semibold">Last msg</th>
                <th className="py-2 px-4" />
              </tr>
            </thead>
            <tbody>
              {items.map((agent) => (
                <AgentListRow
                  key={agent.name}
                  agent={agent}
                  onAction={(a) => handleAction(agent.name, a)}
                  onDelete={() => setToDelete(agent.name)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        open={!!toDelete}
        onOpenChange={(open) => !open && setToDelete(null)}
        title="Delete agent"
        description={`This will stop and remove ${toDelete ?? ''}. This action cannot be undone.`}
        confirmText={toDelete ?? undefined}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={handleConfirmDelete}
      />
      <StepUpDialog
        open={stepUpOpen}
        onOpenChange={setStepUpOpen}
        onSuccess={handleStepUpSuccess}
        title="Confirm deletion"
        description="Re-enter your password to permanently delete this agent."
      />
      {deleteError && (
        <p className="text-sm text-destructive" role="alert">
          {deleteError}
        </p>
      )}
      <GlobalOverlays />
    </div>
  );
}
